import { first, isEmpty, last, uniq } from 'lodash';

import {
  ConsultType,
  SlotType,
  TemplateEntryAppointmentItemDataFragment,
  TemplateEntryItemDataFragment,
  TemplateEntryType,
} from '../types/api/graphql';
import {
  formatDate,
  formatTimeSpanToTime,
  formatTimeToMinutes,
  getTimeSpanMinutes,
} from '../utils/date.utils';
import { ConversationSelectType } from './conversations/enums';
import {
  ScheduleConsultType,
  ScheduleDayPartType,
  ScheduleDayType,
} from './schedule/enums';
import { AgendaAppointmentInput, TemplateEntryItem } from './types';

export const getReminderDefaultSendDate = (value: string) => {
  if (!isEmpty(value)) {
    const date = new Date(value);

    date.setHours(8, 0, 0, 0);

    return date.toISOString();
  }

  return '';
};

export const getScheduleDay = (value: Date | string | null | undefined) => {
  const date = typeof value === 'string' ? new Date(value) : value;

  if (date instanceof Date && !Number.isNaN(date.getFullYear())) {
    return Object.values(ScheduleDayType)[date.getDay() - 1] ?? null;
  }

  return null;
};

/**
 * Monday of ISO-8601 week `weekNr` of week-year `year`: week 1 is the week that holds 4 January,
 * the same rule the server uses (DO-807). A week past the last one of the year rolls into the
 * next year, so 2027-W53 is the Monday of 2028-W01.
 */
export const getScheduleFirstMondayOfWeek = (
  weekNr: number | null | undefined,
  year: number | null | undefined
) => {
  if (!year || !weekNr) {
    return null;
  }

  const fourthOfJanuary = new Date(year, 0, 4);
  const mondayOfWeekOne = 4 - ((fourthOfJanuary.getDay() + 6) % 7);

  return new Date(year, 0, mondayOfWeekOne + (weekNr - 1) * 7);
};

export const getScheduleData = (value: Date | string | null | undefined) => {
  const days = Object.values(ScheduleDayType);
  const dayParts = Object.values(ScheduleDayPartType);

  const date = typeof value === 'string' ? new Date(value) : value;

  const dates: string[] = [];

  if (date instanceof Date && !Number.isNaN(date.getFullYear())) {
    const firstDate = new Date(
      date.setDate(
        date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1)
      )
    );

    [...Array(days.length).keys()].forEach(index => {
      const currentDate = new Date(firstDate);

      currentDate.setDate(currentDate.getDate() + index);

      dates.push(formatDate(currentDate));
    });
  }

  return {
    days,
    dayParts,
    dates,
  };
};

export const getScheduleDataForWeek = (
  weekNr: number | null | undefined,
  year: number | null | undefined
) => getScheduleData(getScheduleFirstMondayOfWeek(weekNr, year));

export const getScheduleRangeForWeek = (
  weekNr: string | number | null | undefined,
  year: string | number | null | undefined
) => {
  let week = new Date().getWeekWithYear();
  let formattedYear = parseInt(first(week.split('-'))!);
  let formattedWeekNr = parseInt(last(week.split('-'))!.replace('W', ''));

  if (year && weekNr) {
    // Through the Monday and back, so a week the year does not have (2027-W53, from a typed URL)
    // becomes the week it lands in instead of an empty one.
    const monday = getScheduleFirstMondayOfWeek(
      parseInt(String(weekNr)),
      parseInt(String(year))
    );

    if (monday && !Number.isNaN(monday.getTime())) {
      week = monday.getWeekWithYear();
      formattedYear = monday.getWeekYear();
      formattedWeekNr = monday.getWeek();
    }
  }

  const data = getScheduleDataForWeek(formattedWeekNr, formattedYear);

  return {
    week,
    weekNr: formattedWeekNr,
    year: formattedYear,
    firstDay: first(data.dates),
    lastDay: last(data.dates),
  };
};

export const getScheduleDays = (
  start: Date | string | null | undefined,
  end: Date | string | null | undefined
) => {
  const startDate = typeof start === 'string' ? new Date(start) : start;

  let endDate = startDate;

  if (end) {
    endDate = typeof end === 'string' ? new Date(end) : end;
  }

  const dates: ScheduleDayType[] = [];

  if (
    startDate instanceof Date &&
    !Number.isNaN(startDate.getFullYear()) &&
    endDate instanceof Date &&
    !Number.isNaN(endDate.getFullYear())
  ) {
    if (!end) {
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 7);
    }

    for (
      let currentDate = new Date(startDate);
      currentDate <= endDate;
      currentDate.setDate(currentDate.getDate() + 1)
    ) {
      const day = getScheduleDay(currentDate);

      if (day) {
        dates.push(day);
      }
    }
  }

  return uniq(dates);
};

export const isTemplateExceptionValidForAppointmentBlock = (
  entry: TemplateEntryItem,
  block: TemplateEntryItemDataFragment
): boolean => {
  const entryMinuteStartTime = getTimeSpanMinutes(entry.minuteStartTime);
  const entryMinuteEndTime = getTimeSpanMinutes(entry.minuteEndTime);
  const blockMinuteStartTime = getTimeSpanMinutes(block.minuteStartTime);
  const blockMinuteEndTime = getTimeSpanMinutes(block.minuteEndTime);

  if (
    entryMinuteStartTime >= blockMinuteStartTime &&
    entryMinuteStartTime <= blockMinuteEndTime &&
    entryMinuteEndTime >= blockMinuteStartTime &&
    entryMinuteEndTime <= blockMinuteEndTime
  ) {
    return true;
  }

  return false;
};

export const getTemplateMinuteLength = (
  template:
    | TemplateEntryItemDataFragment
    | TemplateEntryAppointmentItemDataFragment
) => {
  if (template.type === TemplateEntryType.Exception) {
    return (
      formatTimeToMinutes(formatTimeSpanToTime(template.minuteEndTime)) -
      formatTimeToMinutes(formatTimeSpanToTime(template.minuteStartTime))
    );
  }

  return template.minuteLength;
};

export const getConversationType = (data?: AgendaAppointmentInput) => {
  if (data?.id ?? data?.conversation?.id) {
    return ConversationSelectType.EXISTING;
  }

  return ConversationSelectType.NEW;
};

export const slotTypeToConsultType = (slotType: SlotType) => {
  if (slotType === SlotType.Phone) {
    return ConsultType.Telephone;
  }
  if (slotType === SlotType.Visit) {
    return ConsultType.Visit;
  }
  if (slotType === SlotType.Bulk) {
    return ConsultType.Bulk;
  }
  return ConsultType.Physical;
};

export const consultTypeToSlotType = (consultType: ConsultType) => {
  if (consultType === ConsultType.Telephone) {
    return SlotType.Phone;
  }
  if (consultType === ConsultType.Visit) {
    return SlotType.Visit;
  }
  if (consultType === ConsultType.Bulk) {
    return SlotType.Bulk;
  }

  return SlotType.Common;
};

export const consultTypeToScheduleConsultType = (consultType: ConsultType) => {
  switch (consultType) {
    case ConsultType.Visit:
    case ConsultType.Physical:
    case ConsultType.Else:
      return ScheduleConsultType.PHYSICAL;
    case ConsultType.Telephone:
      return ScheduleConsultType.TELEPHONE;
    case ConsultType.Video:
      return ScheduleConsultType.VIDEO;
    default:
      return null;
  }
};
